import {
    ChangeDetectionStrategy, ChangeDetectorRef,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';

import {ITreeOptions, TreeNode} from '@circlon/angular-tree-component';
import {ITreeNode} from '@circlon/angular-tree-component/lib/defs/api';


import {AcDropDownComponent, AcTreeComponent, AcTreeService, GeneralService, IPDisplayPipe, WSEntities} from 'ac-infra';

@Component({
    selector: 'topology-tree',
    templateUrl: './topology-tree.component.html',
    styleUrls: ['./topology-tree.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopologyTreeComponent implements OnInit {

    @ViewChild('popupDropDown', {static: false}) popupDropDown: AcDropDownComponent;

    @ViewChild('acTree', {static: true}) acTree: AcTreeComponent;
    @ContentChild('nodePrefixIconTemplate', {static: true}) nodePrefixIconTemplate: TemplateRef<any>;

    @Input() treeId = 'topology-tree';

    @Input() treeNodes;
    @Input() checkbox = false;
    @Input() useTreeState = true;
    @Input() disabled = false;
    @Input() restoreExpanded = true;
    @Input() searchPlaceHolder;
    @Input() fakeAllSelected = false;
    @Input() getChildren: (node: TreeNode) => TreeNode[];

    @Input() active: any[];
    @Output() activeChange = new EventEmitter<any[]>();

    @Output() selectChange = new EventEmitter<any[]>();
    @Output() initialized = new EventEmitter<any[]>();
    @Output() updateData = new EventEmitter<any[]>();
    @Output() searchChange = new EventEmitter<string>();

    search: string;
    extendTreeOptions: ITreeOptions;
    statusColors = GeneralService.statusColors;
    entityType;
    dataItem;
    openedNodeId;

    constructor(private acTreeService: AcTreeService, private wsEntities: WSEntities, private ipDisplayPipe: IPDisplayPipe) {}

    ngOnInit() {
        this.extendTreeOptions = {useCheckbox: this.checkbox, useTriState: this.useTreeState, getChildren: this.getChildren};
    }

    toggleMenu = (event: MouseEvent, node: TreeNode, selectedNodeEl) => {
        if (node.data.entityType) {
            this.entityType = node.data.entityType;
        } else {
            this.entityType = !node.realParent ? 'tenant' : 'region';
        }

        this.dataItem = this.wsEntities.getEntity(this.entityType + 's', node.data.id);
        this.openedNodeId = node.data.id;

        this.positionMenuAccordingToSelectedNode(node, selectedNodeEl, false);

        this.popupDropDown.open();
    };

    updateMenuPosition = (nodeEl) => {
        const nodeRect = nodeEl?.getBoundingClientRect();
        const selectedMenuBtn = document.getElementById(this.treeId + '-menuBtn');
        selectedMenuBtn.style.position = 'fixed';
        selectedMenuBtn.style.left = (nodeRect.x + nodeRect.width) + 'px';
        selectedMenuBtn.style.top = (nodeRect.y) + 'px';
    };

    positionMenuAccordingToSelectedNode = (node, nodeEl, doTimeout = true) => {
        if (this.openedNodeId && this.openedNodeId === node.data.id) {
            if (doTimeout) {
                setTimeout(() => {
                    this.updateMenuPosition(nodeEl);
                });
            } else {
                this.updateMenuPosition(nodeEl);
            }
        }

        return false;
    };

    popupDropDownStatusChanged = (opened) => {
        if (!opened) {
            this.openedNodeId = undefined;
        }
    };

    filterByIpAddress = (node: ITreeNode, filter: string): boolean => {
        const ip = node?.data?.ipAddress && this.ipDisplayPipe.transform(node.data.ipAddress);
        return !!ip?.includes(filter);
    }
    filterBySbcSerialNumber = (node: ITreeNode, filter: string): boolean => (node.data.sbcInfo && node.data.sbcInfo.serialNum && node.data.sbcInfo.serialNum.indexOf(filter) > -1);
    topologyFilter = (node: ITreeNode, filter: string): boolean => this.filterByIpAddress(node, filter) || this.filterBySbcSerialNumber(node, filter);

    isNodeMarked = (node: ITreeNode, filter) => {
        return filter && (this.acTreeService.filterByName(node, filter) || this.topologyFilter(node, filter));
    };

    onActiveNodes = (nodes: any[]) => this.activeChange.emit(nodes.map(node => node.data));
    onInitialized = ($event) => this.initialized.next($event);
    onUpdateData = ($event) => {
        this.updateData.next($event);

        if (this.openedNodeId) {
            this.dataItem = this.wsEntities.getEntity(this.entityType + 's', this.openedNodeId);

            setTimeout(() => {
                const nodeEl = document.getElementById(this.treeId + '-node-' + this.openedNodeId);
                const treeEl = document.getElementById(this.treeId);

                const nodeRect = nodeEl?.getBoundingClientRect();
                const treeRect = treeEl?.getBoundingClientRect();
                let nodeIsVisible = false;

                if (nodeRect && treeRect) {
                    nodeIsVisible = (nodeRect.y - treeRect.y) < treeRect.height;
                }

                if (!nodeIsVisible || !nodeEl || !this.dataItem) {
                    this.openedNodeId = undefined;
                    this.popupDropDown.close();
                }
            });
        }
    };

    onSelectNodes(nodes: TreeNode[]) {
        let selectedNodes = [];

        if (this.useTreeState) {
            this.findParents(nodes, selectedNodes);
        } else {
            selectedNodes = nodes;
        }

        this.selectChange.emit(selectedNodes.map(node => node.data));
    }

    findParents(nodes: TreeNode[], selectedNodes) {
        nodes && nodes.forEach((node: TreeNode) => {
            if (node.children && node.isPartiallySelected) {
                this.findParents(node.children, selectedNodes);
            } else if (node.isSelected) {
                selectedNodes.push(node);
            }
        });
    }

    resizeTree = () => this.acTree && this.acTree.resizeTree();
    updateTree = () => this.acTree && this.acTree.updateTree();

    setSelect(nodes: Array<any> | any, state = false, loadFromState = false) {
        const treeModel = this.acTree.tree.treeModel;
        if (!treeModel) {
            return;
        }

        const leafs = {};

        this.acTreeService.updateSelectNodes(nodes || treeModel.roots, state, leafs, treeModel.options.useTriState);
        this.acTree.setSelect(leafs, loadFromState);
    }

    setActive(nodes: Array<any> | any, state = true, expand = false, emit = true) {
        const activeNodeIds = {};
        nodes = GeneralService.oneToMany<any>(nodes);
        nodes.forEach((node) => activeNodeIds[node.id] = state);
        this.acTree.setActive(activeNodeIds, emit);
        expand && this.setExpand(nodes, true, true);
    }

    setExpand(nodes: any[], state = true, loadFromState = false) {
        const parentsIds = {};

        nodes.forEach((node) => {
            if (node.tenantId && !parentsIds[node.tenantId]) {
                parentsIds[node.tenantId] = state;
            }
        });
        this.acTree.setExpand(parentsIds, loadFromState);
    }

    onSearchChange($event: string) {
        this.search = $event;
        this.searchChange.emit(this.search);
    }
}
